import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './lib/auth';
import { useModules } from './hooks/useModules';
import { supabase } from './lib/supabase';
import {
  Home,
  Building,
  MessageSquare,
  Calculator,
  Users,
  Scroll,
  Megaphone,
  CreditCard,
  Files,
  BarChart,
  Bell,
  Wrench,
  Boxes,
  Settings as SettingsIcon,
  Upload
} from 'lucide-react';
import { TenantProvider } from './components/TenantProvider';
import SubscriptionBanner from './components/SubscriptionBanner';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLayout from './pages/admin/Layout';
import LoadingSpinner from './components/LoadingSpinner';

// Lazy load components
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminTenants = lazy(() => import('./pages/admin/Tenants'));
const AdminBilling = lazy(() => import('./pages/admin/Billing'));
const Plans = lazy(() => import('./pages/subscription/Plans'));
const Success = lazy(() => import('./pages/subscription/Success'));
const Messages = lazy(() => import('./pages/Messages'));
const Cancel = lazy(() => import('./pages/subscription/Cancel'));
const AdminLogin = lazy(() => import('./pages/auth/AdminLogin'));
const Auth = lazy(() => import('./pages/Auth'));

// Lazy load all main app components
const Integrations = lazy(() => import('./pages/Integrations'));
const TenantPortal = lazy(() => import('./pages/TenantPortal'));
const Budget = lazy(() => import('./pages/Budget'));
const Payments = lazy(() => import('./pages/Payments'));
const Documents = lazy(() => import('./pages/Documents'));
const Sidebar = lazy(() => import('./components/Sidebar'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Properties = lazy(() => import('./pages/Properties'));
const Tenants = lazy(() => import('./pages/Tenants'));
const Contracts = lazy(() => import('./pages/Contracts'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const Marketing = lazy(() => import('./pages/Marketing'));
const Reports = lazy(() => import('./pages/Reports'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Settings = lazy(() => import('./pages/Settings'));
const FinancialImport = lazy(() => import('./pages/FinancialImport'));

const allNavigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Properties', href: '/properties', icon: Building },
  { name: 'Budget', href: '/budget', icon: Calculator },
  { name: 'Tenants', href: '/tenants', icon: Users },
  { name: 'Contracts', href: '/contracts', icon: Scroll },
  { name: 'Marketing', href: '/marketing', icon: Megaphone },
  { name: 'Payments', href: '/payments', icon: CreditCard },
  { name: 'Messages', href: '/messages', icon: MessageSquare },
  { name: 'Documents', href: '/documents', icon: Files },
  { name: 'Reports', href: '/reports', icon: BarChart },
  { name: 'Maintenance', href: '/maintenance', icon: Wrench },
  { name: 'Integrations', href: '/integrations', icon: Boxes },
  { name: 'Notifications', href: '/notifications', icon: Bell },
  { name: 'Settings', href: '/settings', icon: SettingsIcon },
  { name: 'Financial Import', href: '/financial-import', icon: Upload },
];

function MainLayout({ children, navigation }: { children: React.ReactNode; navigation: any[] }) {
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex h-screen overflow-hidden">
        <Sidebar navigation={navigation} />
        <main className="flex-1 overflow-y-auto bg-gray-100 px-6 py-8">
          <SubscriptionBanner />
          {children}
        </main>
      </div>
    </div>
  );
}

function App() {
  const { isModuleEnabled } = useModules();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      // Initial fetch of unread count
      fetchUnreadCount();
      setLoading(false);

      // Subscribe to notifications changes
      const channel = supabase.channel('notifications');
      
      channel
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'notifications' },
          () => {
            fetchUnreadCount();
          }
        )
        .subscribe();

      return () => {
        channel.unsubscribe();
      };
    }
  }, [user]);

  async function fetchUnreadCount() {
    try {
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .is('read_at', null)
        .eq('user_id', user?.id);
      
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }

  const navigation = allNavigation.filter(item => {
    const moduleId = item.href.replace('/', '') || 'dashboard';
    const enabled = isModuleEnabled(moduleId);
    if (enabled && item.name === 'Notifications') {
      return { ...item, unreadCount };
    }
    return enabled;
  });

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/admin" element={<AdminLogin />} />
        
        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute admin>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="tenants" element={<AdminTenants />} />
          <Route path="billing" element={<AdminBilling />} />
        </Route>

        {/* Subscription routes */}
        <Route path="/subscription/plans" element={<Plans />} />
        <Route path="/subscription/success" element={<Success />} />
        <Route path="/subscription/cancel" element={<Cancel />} />

        {/* Main app routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout navigation={navigation}>
                <Routes>
                  <Route index element={<Dashboard />} />
                  <Route path="properties" element={<Properties />} />
                  <Route path="budget" element={<Budget />} />
                  <Route path="tenants" element={<Tenants />} />
                  <Route path="payments" element={<Payments />} />
                  <Route path="documents" element={<Documents />} />
                  <Route path="contracts" element={<Contracts />} />
                  <Route path="marketing" element={<Marketing />} />
                  <Route path="maintenance" element={<Maintenance />} />
                  <Route path="messages" element={<Messages />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="notifications" element={<Notifications />} />
                  <Route path="settings" element={<Settings />} />
                  <Route path="integrations" element={<Integrations />} />
                  <Route path="tenant-portal" element={<TenantPortal />} />
                  <Route path="financial-import" element={<FinancialImport />} />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

export default App;